import type { ReactElement, ReactNode } from 'react';
import { UI_DEFAULTS } from '@gr/core/platform/config/ui-config';
import { useT } from '../../../../app/di';
import { CalendarView, dayKey, type CalendarDay } from '../model/calendar';
import styles from './CalendarGrid.module.scss';

/** Цвет полоски записи: план, ждёт, вышло, отменено или нейтральный. */
export enum CalendarTone {
  Neutral = 'neutral',
  Accent = 'accent',
  Warning = 'warning',
  Success = 'success',
  Cancelled = 'cancelled',
}

export interface CalendarEntry {
  key: string;
  tone: CalendarTone;
  /** Отмечена пунктиром (конфликт, SCH-RULE-4). */
  flagged?: boolean;
  content: ReactNode;
}

export interface CalendarGridProps {
  view: CalendarView;
  days: readonly CalendarDay[];
  today: CalendarDay;
  /** Месяц на экране: дни соседних месяцев приглушены. */
  month: number;
  entriesOf(day: CalendarDay): readonly CalendarEntry[];
  /** Сколько записей видно в клетке месяца, остальное — «ещё N» (ui.calendar). */
  monthCellLimit?: number;
}

/** Текст записи: время, пояснение (канал или статус) и название поста. */
export function CalendarEntryText({ time, meta, title }: { time: string; meta: string; title: string }): ReactElement {
  return (
    <>
      <span className={styles.time}>{time}</span> <span className={styles.meta}>{meta}</span>
      <span className={styles.postTitle}>{title}</span>
    </>
  );
}

/** SCH-UC-7: сетка дней недели или месяца с записями в клетках. */
export function CalendarGrid({ view, days, today, month, entriesOf, monthCellLimit = UI_DEFAULTS.calendar.monthCellLimit }: CalendarGridProps): ReactElement {
  const { t, locale } = useT();
  return (
    <div className={styles.scroll}>
      <div className={`${styles.grid} ${view === CalendarView.Month ? styles.month : styles.week}`} data-testid="calendar">
        {days.map((day) => {
          const items = entriesOf(day);
          const visible = view === CalendarView.Month ? items.slice(0, monthCellLimit) : items;
          const label = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(day.year, day.month - 1, day.day)));
          const classes = [styles.day, dayKey(day) === dayKey(today) ? styles.today : '', view === CalendarView.Month && day.month !== month ? styles.outside : ''].join(' ');
          return (
            <section key={dayKey(day)} className={classes} aria-label={label}>
              <h3 className={styles.dayTitle}>{label}</h3>
              <ul className={styles.items}>
                {visible.map((entry) => (
                  <li key={entry.key} className={`${styles.item} ${styles[entry.tone] ?? ''} ${entry.flagged ? styles.flagged : ''}`}>
                    {entry.content}
                  </li>
                ))}
                {items.length > visible.length ? <li className="u-muted u-small">{t('scheduling.calendar.more', { n: items.length - visible.length })}</li> : null}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
